/**
 * 概况卡片（设备统计 / 视频统计）多租户适配
 *
 * 与子应用 iot-web 保持同一套约定：
 * - URL 带 `tenant=all`（多租户聚合）→ 概况接口走 `/multiTenant/` 新路径，
 *   并带 `X-Tenant-Data-Scope: accessible_aggregate`；
 * - 其它场景 → 老路径（后端逻辑未改，全量/原行为）；
 * - 所有请求带 `X-Tenant-Id`（localStorage 有值才带）。
 *
 * 注意：基座是 hash 路由，query 在 `#` 之后，同时兼容 query 在 `#` 之前。
 */

export const TENANT_QUERY_KEY = "tenant";

export const ALL_TENANT_SCOPE = "all";

/** 基座 / 子应用共用的租户存储键 */
export const TENANT_ID_STORAGE_KEY = "X-Tenant-Id";

/** 可访问租户汇总 */
export const TENANT_SCOPE_HEADER = "X-Tenant-Data-Scope";

export const AGGREGATE_SCOPE = "accessible_aggregate";

export type TenantScope = "current" | "all";

const decode = (value: string): string => {
  try {
    return decodeURIComponent(value.replace(/\+/g, " "));
  } catch (e) {
    return value;
  }
};

/** 从 `?a=1&b=2` 或 `#/path?a=1` 中解析 query */
const parseQuery = (raw?: string): Record<string, string> => {
  const result: Record<string, string> = {};
  if (!raw) {
    return result;
  }
  const queryIndex = raw.indexOf("?");
  if (queryIndex < 0) {
    return result;
  }
  let body = raw.slice(queryIndex + 1);
  const hashIndex = body.indexOf("#");
  if (hashIndex >= 0) {
    body = body.slice(0, hashIndex);
  }
  body.split("&").forEach((pair) => {
    if (!pair) {
      return;
    }
    const eqIndex = pair.indexOf("=");
    const key = decode(eqIndex >= 0 ? pair.slice(0, eqIndex) : pair).trim();
    if (!key) {
      return;
    }
    result[key] = eqIndex >= 0 ? decode(pair.slice(eqIndex + 1)).trim() : "";
  });
  return result;
};

/** 当前页面的租户作用域 */
export function getTenantScope(): TenantScope {
  if (typeof window === "undefined") {
    return "current";
  }
  const candidates = [window.location.hash, window.location.search];
  for (let i = 0; i < candidates.length; i += 1) {
    const scope = parseQuery(candidates[i])[TENANT_QUERY_KEY];
    if (scope && scope.toLowerCase() === ALL_TENANT_SCOPE) {
      return "all";
    }
  }
  return "current";
}

export const isAllTenantScope = (): boolean => getTenantScope() === "all";

/** 租户请求头：localStorage 有值才构建 */
export function getTenantIdHeader(): Record<string, string> {
  if (typeof window === "undefined") {
    return {};
  }
  try {
    const tenantId = localStorage.getItem(TENANT_ID_STORAGE_KEY) || "";
    return tenantId ? { [TENANT_ID_STORAGE_KEY]: tenantId } : {};
  } catch (e) {
    return {};
  }
}

/** 多租户聚合路径（仅这些路径需要 X-Tenant-Data-Scope） */
export const isMultiTenantApiPath = (url?: string): boolean =>
  !!url && /\/multiTenant\//i.test(url);

/**
 * 概况卡片接口：老路径 → 多租户路径
 * 逐条精确映射，不做字符串拼接（multiTenant 段的位置因接口而异）。
 */
const OVERVIEW_CARD_PATH_MAP: Record<string, string> = {
  "/api/dashboard/_multi": "/api/dashboard/multiTenant/_multi",
  "/api/device/number": "/api/device/multiTenant/number",
  "/api/device/protocol/exit": "/api/device/multiTenant/protocol/exit",
  "/api/device-product/_count": "/api/device-product/multiTenant/_count",
  "/api/device/instance/online/_count":
    "/api/device/instance/online/multiTenant/_count",
  "/api/device/instance/offline/_count":
    "/api/device/instance/offline/multiTenant/_count",
  "/api/device/instance/notActive/_count":
    "/api/device/instance/notActive/multiTenant/_count",
  "/api/geo/object/device/_search/geo.json":
    "/api/geo/object/device/multiTenant/_search/geo.json",
  "/api/media/device/_count": "/api/media/device/multiTenant/_count",
  "/api/media/channel/_count": "/api/media/channel/multiTenant/_count",
  "/api/media/channel/playing/agg":
    "/api/media/channel/playing/multiTenant/agg",
  "/api/media/record/file/agg":
    "/api/media/record/file/multiTenant/agg",
};

/**
 * `/api/dashboard/_multi` 是共享接口（告警卡片也在用），
 * 仅对概况维度切新路径，避免误伤 `dashboard: 'alarm'` 等卡片。
 */
const MULTI_TENANT_DASHBOARD_DIMENSIONS = ["device", "media_stream"];

const isOverviewDashboardRequest = (params?: any): boolean => {
  const data = params?.data;
  const list = Array.isArray(data) ? data : [data];
  return list.some(
    (item) =>
      item &&
      MULTI_TENANT_DASHBOARD_DIMENSIONS.indexOf(String(item.dashboard)) > -1
  );
};

/**
 * 卡片请求地址解析：
 * - 非 `tenant=all` → 原样返回（走老路径）
 * - 已是 `/multiTenant/` → 原样返回（后端下发的新配置）
 * - 命中映射表 → 返回多租户路径
 */
export function resolveCardApiUrl(url: string, params?: any): string {
  if (!url || !isAllTenantScope() || isMultiTenantApiPath(url)) {
    return url;
  }
  const queryIndex = url.indexOf("?");
  const path = queryIndex >= 0 ? url.slice(0, queryIndex) : url;
  const query = queryIndex >= 0 ? url.slice(queryIndex) : "";

  if (path === "/api/dashboard/_multi" && !isOverviewDashboardRequest(params)) {
    return url;
  }

  const target = OVERVIEW_CARD_PATH_MAP[path];
  return target ? `${target}${query}` : url;
}
